import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { OIDCProviderScope } from '../../../../../shared/model-typings/oidcprovider';
import { useDresser } from '../../../../contexts/DrawerContext';
import { useDeleteTrustRelationship, useTrustRelationships } from '../../../../hooks/api/oidc';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import { useCanCreateTrustRelationship } from '../../shared/permissions';
import type { OIDCTrustRelationship } from '../../../../../shared/model-typings/oidctrustrelationship';

import ModalCreateTrustRelationship from './ModalCreateTrustRelationship';
import TrustRelationshipActionMenu from './TrustRelationshipActionMenu';
import TrustRelationshipDetailsDrawer from './TrustRelationshipDetailsDrawer';

type SortingRule = {
  id: string;
  desc: boolean;
};

const DEFAULT_SORT_MODE: SortingRule = { id: 'providerId', desc: false };

type TrustRelationshipsTableProps = {
  userId?: number;
  providerId?: number;
  currentTeamId?: number;
  showCreateButton?: boolean;
};

const TrustRelationshipsTable: VoidFunctionComponent<TrustRelationshipsTableProps> = ({
  userId,
  providerId,
  currentTeamId,
  showCreateButton = false,
}) => {
  const [sortMode, setSortMode] = useState<SortingRule>(DEFAULT_SORT_MODE);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { pushDrawer } = useDresser();
  const canCreateTrustRelationship = useCanCreateTrustRelationship();
  const {
    data: pages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useTrustRelationships({ userId, providerId });
  const deleteTrustRelationship = useDeleteTrustRelationship();

  // Auto-pagination
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  const allTrustRelationships = useMemo(
    () => pages?.pages.flatMap((page) => page.results) ?? [],
    [pages],
  );

  const matchingTrustRelationships = useMemo(
    () => allTrustRelationships.filter((trustRelationship) =>
      trustRelationship.userId?.toString().includes(searchValue.toLowerCase()) ||
      trustRelationship.scopeId?.toString().includes(searchValue.toLowerCase()),
    ),
    [allTrustRelationships, searchValue],
  );

  const handleDelete = useCallback((id: number) => {
    deleteTrustRelationship.mutate(id);
  }, [deleteTrustRelationship]);

  const handleView = useCallback((id: number) => {
    const trustRelationship = allTrustRelationships.find((tr) => tr.id === id);
    if (trustRelationship) {
      pushDrawer(<TrustRelationshipDetailsDrawer trustRelationship={trustRelationship} />);
    }
  }, [allTrustRelationships, pushDrawer]);

  const ActionsCell = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ original }: { original: OIDCTrustRelationship }) => (
      <TrustRelationshipActionMenu
        trustRelationship={original}
        onDeleteTrustRelationship={handleDelete}
        onViewTrustRelationship={handleView}
      />
    ),
    [handleDelete, handleView],
  );

  const columns = useMemo(() => {
    const baseColumns = [];
    if (!providerId) {
      baseColumns.push({
        id: 'providerId',
        Header: (
          <TableHeader title="Provider ID" sort={{ id: 'providerId', currentMode: sortMode }} />
        ),
        accessor: 'providerId',
        width: 150,
        resizable: false,
      });
    }
    if (!userId) {
      baseColumns.push({
        id: 'userId',
        Header: (
          <TableHeader title="User ID" sort={{ id: 'userId', currentMode: sortMode }} />
        ),
        accessor: 'userId',
        width: 150,
        resizable: false,
      });
    }

    baseColumns.push(
      {
        id: 'scope',
        Header: (
          <TableHeader title="Scope" sort={{ id: 'scope', currentMode: sortMode }} />
        ),
        accessor: ({ scope }: OIDCTrustRelationship) =>
          scope === OIDCProviderScope.ORG ? 'Organization' : 'Team',
        width: 150,
        resizable: false,
      },
      {
        id: 'scopeId',
        Header: (
          <TableHeader title="Scope ID" sort={{ id: 'scopeId', currentMode: sortMode }} />
        ),
        accessor: 'scopeId',
        width: 150,
        resizable: false,
      },
      {
        id: 'actions',
        Header: <TableHeader title="Actions" />,
        resizable: false,
        width: 100,
        Cell: ActionsCell,
      },
    );

    return baseColumns;
  }, [providerId, userId, sortMode, ActionsCell]);

  return (
    <>
      <SettingsPageTableActions>
        {showCreateButton && (
          <SettingsPageTableActions.TooltipButton
            id="add-trust-relationship-button"
            type="secondary"
            size="large"
            showTooltip={!canCreateTrustRelationship}
            tooltip="You are not permitted to add a trust relationship."
            onClick={() => setIsCreateModalOpen(true)}
            isDisabled={!canCreateTrustRelationship}
          >
            Add a trust relationship
          </SettingsPageTableActions.TooltipButton>
        )}
        <SettingsPageTableActions.SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search by username or team name"
          label="search-by-username"
        />
      </SettingsPageTableActions>
      <SimpleReactTable
        isSortable
        data={matchingTrustRelationships}
        columns={columns}
        defaultSortMode={[DEFAULT_SORT_MODE]}
        onSortedChanged={setSortMode}
        showPagination
        cacheId="trustRelationships"
        isLoading={isLoading}
      />
      {providerId && (
        <ModalCreateTrustRelationship
          isOpen={isCreateModalOpen}
          onHide={() => setIsCreateModalOpen(false)}
          providerId={providerId}
          currentTeamId={currentTeamId}
        />
      )}
    </>
  );
};

export default TrustRelationshipsTable;
